import { useEffect, useRef, useState } from 'react'

import './index.scss'

const D2R = Math.PI / 180
const SUN_AZIM = 4 // matches P.sunAzim in the engine
const HERO = { yaw: (SUN_AZIM - 37) * D2R, pitch: -5.5 * D2R, y: 2.4 }
const LOOK_RANGE = {
  look: { yaw: 35 * D2R, pitch: 10 * D2R },
  explore: { yaw: 70 * D2R, pitch: 20 * D2R },
}
const MIN_Y = 1.5
const MAX_WHEEL_Y = 120
const MAX_SCROLL_Y = 60

const shouldEnable = () => {
  const force = new URLSearchParams(window.location.search).get('ocean')
  if (force === '0' || !navigator.gpu) return false
  if (force === '1') return true
  // Automated browsers, including the build-time prerender, get the static background
  if (navigator.webdriver) return false
  return (
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    window.innerWidth >= 1024
  )
}

const pageScrollable = () =>
  document.documentElement.scrollHeight > window.innerHeight + 1

const ease = (from, to, rate, dt) => from + (to - from) * (1 - Math.exp(-rate * dt))

const OceanBackground = () => {
  // Decided after mount so the first render matches the prerendered HTML.
  const [enabled, setEnabled] = useState(false)
  const [status, setStatus] = useState('loading') // loading | on | failed
  const [mode, setMode] = useState('look') // look | explore
  const canvasRef = useRef()
  const modeRef = useRef(mode)
  const pointer = useRef({ x: 0.5, y: 0.5 })
  const wheelY = useRef(HERO.y)

  modeRef.current = mode

  useEffect(() => {
    setEnabled(shouldEnable())
  }, [])

  useEffect(() => {
    if (!enabled) return
    let ocean = null
    let cancelled = false
    let time = 0
    let initialised = false
    let guard = { warmup: 1.5, elapsed: 0, frames: 0, done: false }

    const fail = (err) => {
      if (cancelled) return
      console.warn('[ocean] falling back to the static background:', err && err.message)
      setStatus('failed')
      setTimeout(() => ocean && ocean.destroy(), 800)
    }

    // Render at native resolution like upstream; measure once after start-up and give up below ~15 fps.
    const checkSpeed = (dt) => {
      if (guard.done || document.hidden || dt >= 0.2) return
      if (guard.warmup > 0) {
        guard.warmup -= dt
        return
      }
      guard.elapsed += dt
      guard.frames++
      if (guard.elapsed < 2) return
      guard.done = true
      const avg = guard.elapsed / guard.frames
      if (avg > 0.066) fail(new Error(`too slow (${(avg * 1000).toFixed(1)} ms per frame)`))
    }

    const onFrame = (dt, cam) => {
      time += dt
      if (!initialised) {
        cam.yaw = HERO.yaw
        cam.pitch = HERO.pitch
        cam.y = HERO.y
        initialised = true
      }
      if (ocean) checkSpeed(dt)

      // Follow the mouse around the hero view, with a gentle boat-like sway on top.
      const look = LOOK_RANGE[modeRef.current]
      const yaw =
        HERO.yaw + (pointer.current.x - 0.5) * 2 * look.yaw + Math.sin(time * 0.9) * 0.6 * D2R
      const pitch =
        HERO.pitch + (0.5 - pointer.current.y) * 2 * look.pitch + Math.sin(time * 1.19) * 0.4 * D2R

      let y
      if (pageScrollable()) {
        const max = document.documentElement.scrollHeight - window.innerHeight
        const progress = Math.min(1, Math.max(0, window.scrollY / max))
        y = HERO.y * Math.pow(MAX_SCROLL_Y / HERO.y, progress)
      } else {
        y = wheelY.current
      }
      y += Math.sin(time * 0.82) * 0.08

      cam.yaw = ease(cam.yaw, yaw, 3, dt)
      cam.pitch = ease(cam.pitch, pitch, 3, dt)
      cam.y = ease(cam.y, y, 2.5, dt)
    }

    import('./engine/engine')
      .then(({ createOcean }) =>
        createOcean(canvasRef.current, {
          onFrame,
          onError: fail,
          onFirstFrame: () => {
            if (!cancelled) setStatus('on')
          },
        })
      )
      .then((instance) => {
        if (cancelled) instance.destroy()
        else ocean = instance
      })
      .catch(fail)

    return () => {
      cancelled = true
      if (ocean) ocean.destroy()
    }
  }, [enabled])

  // Input: look-around, wheel height.
  useEffect(() => {
    if (status !== 'on') return

    const onMove = (e) => {
      pointer.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }
    }
    // Ease back to the hero view when the mouse leaves the window.
    const onLeave = () => {
      pointer.current = { x: 0.5, y: 0.5 }
    }
    const onWheel = (e) => {
      if (pageScrollable()) return
      const next = wheelY.current * Math.exp(e.deltaY * 0.0015)
      wheelY.current = Math.min(MAX_WHEEL_Y, Math.max(MIN_Y, next))
    }

    window.addEventListener('mousemove', onMove, { passive: true })
    document.documentElement.addEventListener('mouseleave', onLeave)
    window.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      window.removeEventListener('mousemove', onMove)
      document.documentElement.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('wheel', onWheel)
    }
  }, [status])

  // Explore mode: any click or Esc returns to the site.
  useEffect(() => {
    if (mode !== 'explore') return

    const exit = () => setMode('look')
    const onKey = (e) => {
      if (e.key === 'Escape') exit()
    }

    window.addEventListener('pointerdown', exit, { passive: true, capture: true })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', exit, { capture: true })
      window.removeEventListener('keydown', onKey)
    }
  }, [mode])

  // Page styles for the ocean theme are scoped under these classes.
  useEffect(() => {
    const root = document.documentElement.classList
    root.toggle('ocean-active', status === 'on')
    root.toggle('ocean-explore', status === 'on' && mode === 'explore')
    return () => root.remove('ocean-active', 'ocean-explore')
  }, [status, mode])

  if (!enabled) return null

  return (
    <>
      <canvas
        ref={canvasRef}
        className={`ocean-canvas${status === 'on' ? ' is-visible' : ''}`}
        aria-hidden="true"
      />
      {status === 'on' && (
        <>
          <div
            className={`ocean-hint${mode === 'explore' ? ' is-visible' : ''}`}
            aria-hidden="true"
          >
            Click or Esc to return
          </div>
          <div className="ocean-corner">
            {mode === 'look' && (
              <button type="button" className="ocean-explore-btn" onClick={() => setMode('explore')}>
                Explore the ocean
              </button>
            )}
          </div>
        </>
      )}
    </>
  )
}

export default OceanBackground
